import React from 'react'
import { Helmet } from 'react-helmet'
import config from '../utils/siteConfig'

const SEO = ({
  postNode,
  pagePath,
  postSEO,
  pageSEO,
  customTitle,
  homePage,
  faqPageSchema,
}) => {
  let title
  let description
  let image
  let imgWidth
  let imgHeight
  let pageUrl

  // Set Default OpenGraph Parameters for Fallback
  title = config.siteTitle
  description = config.siteDescription
  image = config.siteUrl + config.shareImage
  imgWidth = config.shareImageWidth
  imgHeight = config.shareImageHeight
  pageUrl = config.siteUrl

  if (customTitle) {
    title = postNode.title
    pageUrl = config.siteUrl + '/' + pagePath + '/'
  }

  // Replace with Page Parameters if post or page
  if (postSEO || pageSEO) {
    title = postNode.title
    description = postNode.metaDescription?.internal?.content

    pageUrl = config.siteUrl + '/' + pagePath + '/'
  }
  // Use Hero Image for OpenGraph
  if (postSEO) {
    image = 'https:' + postNode?.heroImage?.ogimg.src
    imgWidth = postNode?.heroImage?.ogimg.width
    imgHeight = postNode?.heroImage?.ogimg.height
  }

  // Default Website Schema
  const schemaOrgJSONLD = [
    {
      '@context': 'http://schema.org',
      '@type': 'WebSite',
      url: config.siteUrl,
      name: config.siteTitle,
      alternateName: config.siteTitleAlt ? config.siteTitleAlt : '',
      logo: config.siteLogo,
    },
  ]

  if (homePage) {
    schemaOrgJSONLD.push({
      '@context': 'http://www.schema.org',
      '@type': 'ProfessionalService',
      name: 'PrintAWorld',
      url: 'https://prtwd.com/',
      logo:
        'https://images.ctfassets.net/dovsnf6qrcem/5gThV6LOVi4vo7eXBZc0iG/8f7cb3557057ab9469eae3f8331f270e/printaworld-new-logo-black.jpg',
      image:
        'https://images.ctfassets.net/dovsnf6qrcem/2yb1IzZ4iSQYuzlaL7dsdI/3a802715b2b015d209c1c2ba00ce1e6b/_DSC3097.jpg?w=4000&q=100',
      description:
        "PrintAWorld helps artists, agencies and engineers turn their ideas its physical form. We use rapid prototyping technologies such as 3D printing, 3D Scanning, 3D modeling, laser cutting, and CNC milling to build your next big idea. We're a fabrication company first, and a 3D printing company second. That's why, unlike other 3D printing companies, we decided not to automate our quoting process. With PrintAworld, you get to talk to an actual human. 3D printing is our bread and butter. We can do it fast, high-quality, and within budget. We help fabricate complex art concepts for artists who need to reach tight deadlines. We've helped brands and agencies create 3D printed props, architecture models, jewelry and even furniture. But we don't just 3D print, we want to understand you and your project. And you can't get that from automated solutions. We would love to hear about your concept so we can help you in any way we can. Whether that's through helping you pick the right material, fixing your 3D model, or suggesting a better alternative solution to your project, we're here for you. We know it sounds cliché but we pride ourselves in making the best 3D print. We spend hours cleaning and sanding to get it to our high standards. We use high quality materials like SOMOS Resin to give you a high-detailed and smooth finish. We offer assembly for complex and large scale 3D prints. We offer metal plating and painting so you don't have to. We want to help you get your idea into production so you can focus on the bigger picture. If you're starting from a sketch or inspiration, we can digitize it with the help of our 3D Artist and CAD Designer. From sculptures to commercial products, we can design it. If you have an object that you want to replicate, we also offer 3D Scanning service. We can 3D scan small to large objects with our state-of-the-art 3D scanner. This is perfect for existing sculptures, hand-made products and more. Aside from additive manufacturing, we also offer laser cutting and CNC milling. With laser cutting, we help brands create memorable giveaways. We can do acrylic keychains, wooden coasters, art pieces and more. You name it, we can make it. Our laser cutting machine can cut up to 40\"x28\". It's also a good alternative to 3D printing if it's a flat design. With CNC milling, we can fabricate large sculptures, signs and huge props. We recommend this if the project is too expensive to 3D print or too large to laser cut.",
      address: {
        '@type': 'PostalAddress',
        streetAddress: '394 McGuinness Blvd',
        addressLocality: 'Brooklyn',
        addressRegion: 'New York',
        postalCode: '11222',
        addressCountry: 'USA',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: '40.736524',
        longitude: '-73.952458',
      },
      hasMap: 'https://goo.gl/maps/c4eR2RcMwwtjgFQJ6',
      openingHours: 'Mo, Tu, We, Th, Fr 10:00-18:30 Sa, Su 12:00-18:30',
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'Customer support',
        telephone: '+1 9292139604',
      },
      telephone: '+1 9292139604',
      priceRange: '$$',
    })

    pageUrl = config.siteUrl + '/'
  }

  // Blog Post Schema
  if (postSEO) {
    schemaOrgJSONLD.push(
      {
        '@context': 'http://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            item: {
              '@id': config.siteUrl,
              name: config.siteTitle,
            },
          },
          {
            '@type': 'ListItem',
            position: 2,
            item: {
              '@id': pageUrl,
              name: title,
            },
          },
        ],
      },
      {
        '@context': 'http://schema.org',
        '@type': 'BlogPosting',
        url: pageUrl,
        name: title,
        alternateName: config.siteTitleAlt ? config.siteTitleAlt : '',
        headline: title,
        image: {
          '@type': 'ImageObject',
          url: image,
          width: imgWidth,
          height: imgHeight,
        },
        author: {
          '@type': 'Person',
          name: config.author,
          url: config.authorUrl,
        },
        publisher: {
          '@type': 'Organization',
          name: config.publisher,
          url: config.siteUrl,
        },
        datePublished: postNode.publishDateISO,
        mainEntityOfPage: pageUrl,
      }
    )
  }

  // Page SEO Schema
  if (pageSEO) {
    schemaOrgJSONLD.push({
      '@context': 'http://schema.org',
      '@type': 'WebPage',
      url: pageUrl,
      name: title,
    })
  }

  // Add addition jsonld if there's any
  if (faqPageSchema) {
     schemaOrgJSONLD.push(faqPageSchema)
  }

  return (
    <Helmet>
      {/* General tags */}
      <meta name="image" content={image} />
      <meta name="description" content={description} />

      {/* Schema.org tags */}
      <script type="application/ld+json">
        {JSON.stringify(schemaOrgJSONLD)}
      </script>

      {/* OpenGraph tags */}
      <meta property="og:title" content={title} />
      {postSEO ? <meta property="og:type" content="article" /> : null}

      <meta property="og:url" content={pageUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content={imgWidth} />
      <meta property="og:image:height" content={imgHeight} />
      <meta property="og:description" content={description} />

      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta
        name="twitter:creator"
        content={config.userTwitter ? config.userTwitter : ''}
      />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:description" content={description} />
    </Helmet>
  )
}

export default SEO
